import {
  HttpErrorResponse,
  HttpInterceptorFn
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  catchError,
  switchMap,
  throwError
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/service/auth.service';
import {
  adminTokenRefreshFailed,
  adminTokenRefreshSucceeded
} from '../auth/store/auth.actions';

const STATIC_FILE_PATTERN = /\.(?:html|css|js|mjs|map|png|jpg|jpeg|gif|svg|webp|ico|woff2?|ttf|json)$/i;

export function isProtectedApiRequest(url: string): boolean {
  const normalizedUrl = url.toLowerCase();

  if (
    normalizedUrl.startsWith('/assets/')
    || normalizedUrl.includes('/assets/')
    || normalizedUrl.startsWith('/public/')
    || normalizedUrl.includes('/public/')
  ) {
    return false;
  }

  if (STATIC_FILE_PATTERN.test(normalizedUrl) && !normalizedUrl.includes('/api/')) {
    return false;
  }

  return normalizedUrl.startsWith('/api/') || normalizedUrl.includes('/api/');
}

export function getStoredBackendToken(authService: AuthService): string | null {
  const token = authService.getStoredAuthToken()?.trim();
  const decodedToken = authService.decodeAuthToken(token);

  if (decodedToken === null) {
    return null;
  }

  return decodedToken.exp > Math.floor(Date.now() / 1000)
    ? token!
    : null;
}

export function isAuthenticationRequest(url: string): boolean {
  return url.toLowerCase().includes('/api/authentication/');
}

function resolveRefreshError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return error.error?.description ?? 'Session refresh failed';
  }

  return error instanceof Error ? error.message : 'Session refresh failed';
}

export const headerInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const store = inject(Store);
  const hasExplicitAuthorization = req.headers.has('Authorization');
  const isProtectedRequest = isProtectedApiRequest(req.url);
  const headers: Record<string, string> = { 'Ocp-Apim-Subscription-Key': environment.ocpApimSubscriptionKey };

  if (!hasExplicitAuthorization && isProtectedRequest) {
    const token = getStoredBackendToken(authService);
    if (token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const modified = req.clone({ setHeaders: headers });
  return next(modified).pipe(
    catchError((error: unknown) => {
      const canRefresh = error instanceof HttpErrorResponse
        && (error.status === 401 || error.status === 440)
        && isProtectedRequest
        && !hasExplicitAuthorization
        && !isAuthenticationRequest(req.url);

      if (!canRefresh) {
        return throwError(() => error);
      }

      const refreshRequest$ = authService.refreshAuthToken().pipe(
        catchError((refreshError: unknown) => {
          const message = resolveRefreshError(refreshError);

          store.dispatch(adminTokenRefreshFailed({ error: message }));
          return throwError(() => refreshError);
        })
      );

      return refreshRequest$.pipe(
        switchMap((session) => {
          store.dispatch(adminTokenRefreshSucceeded(session));

          const authorizationHeaders = { Authorization: `Bearer ${session.token}` };

          return next(modified.clone({ setHeaders: authorizationHeaders }));
        })
      );
    })
  );
};
